import type { Term } from '@prisma/client'
import { useCallback, useEffect, useState } from 'react'
import type { BaseEditor, Editor, NodeEntry, Range } from 'slate'
import { Text } from 'slate'

export type GlossaryTerm = Pick<Term, 'name' | 'explanation'>

export type GlossaryEditor = {
  glossaryPluginInstalled?: true
  glossaryTerms?: GlossaryTerm[]
  glossaryMap?: Map<string, GlossaryTerm>
  projectId?: string
}

type DecorateType = (entry: NodeEntry) => Range[]

async function getGlossary(projectId: string): Promise<GlossaryTerm[]> {
  const response = await fetch(`/api/glossary?projectId=${projectId}`)

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export const withGlossary = {
  bind({ projectId }: { projectId: string }) {
    return <T extends BaseEditor>(editor: T) => {
      const e = editor as T & Editor & GlossaryEditor
      const { useInitializer } = e

      e.glossaryPluginInstalled = true
      e.projectId = projectId

      e.useInitializer = function useInitializerFunc({ decorators, ...rest }) {
        const glossaryDecorator = useGlossaryDecorator(e)
        return useInitializer({
          decorators: [...decorators, glossaryDecorator],
          ...rest,
        })
      }

      return e
    }
  },
}

function useGlossaryDecorator(editor: Editor & GlossaryEditor): DecorateType {
  const [glossaryMap, setGlossaryMap] = useState<Map<string, GlossaryTerm>>(
    new Map(),
  )

  useEffect(() => {
    if (!editor.glossaryPluginInstalled) {
      return
    }

    if (!editor?.projectId) {
      console.warn('ProjectId not available for glossary terms fetch')
      return
    }

    const fetchTerms = async () => {
      try {
        const glossary = await getGlossary(editor.projectId!)
        setGlossaryMap(new Map(glossary.map((term) => [term.name, term])))
      } catch (error) {
        console.error('Failed to fetch glossary terms:', error)
      }
    }

    fetchTerms()
  }, [editor])

  return useCallback(
    ([node, path]) => {
      if (
        !editor.glossaryPluginInstalled ||
        !glossaryMap ||
        !Text.isText(node)
      ) {
        return []
      }

      const decorations: Range[] = []
      const text = node.text
      let match

      // regex to find words and their position
      const wordRegex = /[\w]+/g

      while ((match = wordRegex.exec(text)) !== null) {
        const word = match[0]
        if (word.length > 1) {
          const term = glossaryMap.get(word)
          if (term) {
            decorations.push({
              anchor: { path, offset: match.index },
              focus: { path, offset: match.index + word.length },
              glossaryTerm: term,
            })
          }
        }
      }

      return decorations
    },
    [editor, glossaryMap],
  )
}
